/**
 * @file animation.js
 * @desc This module contains Animation class definition which is adopted PIXI.MovieClips
 * @author Igor Kling <kling.igor.ksi@gmail.com>
 * @version 1.0.dev
 * @copyright 2016 (c) KSI Corporation
 * */

(/** @lends <global> */function (global) {
    'use strict';

    /**
     * A Animation is a simple way to display an animation depicted by a list of textures.
     *
     * ```js
     * var alienImages = ["image_sequence_01.png","image_sequence_02.png","image_sequence_03.png","image_sequence_04.png"];
     * var textureArray = [];
     *
     * for (var i=0; i < 4; i++)
     * {
     *      var texture = PIXI.Texture.fromImage(alienImages[i]);
     *      textureArray.push(texture);
     * };
     *
     * var mc = new PIXI.Animation(textureArray);
     * ```
     *
     *
     * @class
     * @extends PIXI.Sprite
     * @memberof PIXI.extras
     * @param textures {PIXI.Texture[]|FrameObject[]} an array of {@link PIXI.Texture} or frame objects that make up the animation
     */
    function Animation(textures) {
        PIXI.Sprite.call(this, textures[0] instanceof PIXI.Texture ? textures[0] : textures[0].texture);
        
        /**
         * @private
         */
        this._textures = null;
        
        /**
         * @private
         */
        this._durations = null;
        
        this.textures = textures;
        
        /**
         * The speed that the Animation will play at. Higher is faster, lower is slower
         *
         * @member {number}
         * @default 1
         */
        this.animationSpeed = 1;
        
        /**
         * Whether or not the movie clip repeats after playing.
         *
         * @member {boolean}
         * @default true
         */
        this.loop = true;
        
        /**
         * Function to call when a Animation finishes playing
         *
         * @method
         * @memberof PIXI.extras.Animation#
         */
        this.onComplete = null;
        
        /**
         * Elapsed time since animation has been started, used internally to display current texture
         *
         * @member {number}
         * @private
         */
        this._currentTime = 0;
        
        /**
         * Indicates if the Animation is currently playing
         *
         * @member {boolean}
         * @readonly
         */
        this.playing = false;
    }
    
    // constructor
    Animation.prototype = Object.create(PIXI.Sprite.prototype);
    Animation.prototype.constructor = Animation;
    // module.exports = Animation;
    
    Object.defineProperties(Animation.prototype, {
        /**
         * totalFrames is the total number of frames in the Animation. This is the same as number of textures
         * assigned to the Animation.
         *
         * @member {number}
         * @memberof PIXI.extras.Animation#
         * @default 0
         * @readonly
         */
        totalFrames: {
            get: function() {
                return this._textures.length;
            }
        },
        
        /**
         * The array of textures used for this Animation
         *
         * @member {PIXI.Texture[]}
         * @memberof PIXI.extras.Animation#
         *
         */
        textures: {
            get: function() {
                return this._textures;
            },
            set: function(value) {
                if (value[0] instanceof PIXI.Texture) {
                    this._textures = value;
                    this._durations = null;
                }
                else {
                    this._textures = [];
                    this._durations = [];
                    for(var i = 0; i < value.length; i++) {
                        this._textures.push(value[i].texture);
                        this._durations.push(value[i].time);
                    }
                }
            }
        },
        
        /**
         * The MovieClips current frame index
         *
         * @member {number}
         * @memberof PIXI.extras.Animation#
         * @readonly
         */
        currentFrame: {
            get: function() {
                var currentFrame = Math.floor(this._currentTime) % this._textures.length;
                if (currentFrame < 0) {
                    currentFrame += this._textures.length;
                }
                return currentFrame;
            }
        }
        
    });
    
    /**
     * Stops the Animation
     *
     */
    Animation.prototype.stop = function() {
        if (!this.playing) {
            return;
        }
        
        this.playing = false;
        PIXI.ticker.shared.remove(this.update, this);
    };
    
    /**
     * Plays the Animation
     * @param {Number} framesToPlay optional number of frames to play
     */
    Animation.prototype.play = function(framesToPlay) {
        if (this.playing) {
            return;
        }
        
        if (framesToPlay !== undefined) {
            this._startFrame = this._currentTime % 1 | 0;
            
            if (framesToPlay > this._textures.length - this._startFrame - 1) {
                framesToPlay = this._textures.length - this._startFrame - 1;
            }
            
            this._framesToPlay = framesToPlay;
        }
        
        this.playing = true;
        PIXI.ticker.shared.add(this.update, this);
    };
    
    /**
     * Stops the Animation and goes to a specific frame
     *
     * @param frameNumber {number} frame index to stop at
     */
    Animation.prototype.gotoAndStop = function(frameNumber) {
        this.stop();
        
        var _curFrame = this.currentFrame;
        
        this._currentTime = frameNumber;
        
        this._texture = this._textures[this.currentFrame];
        this._textureID = -1;
    
        if (_curFrame !== this.currentFrame && this.onFrameChange) {
            this.onFrameChange(this.currentFrame);
        }
    };
    
    /**
     * Goes to a specific frame and begins playing the Animation
     *
     * @param frameNumber {Number} frame index to start at
     * @param framesToPlay {Number} optional number of frames to play
     */
    Animation.prototype.gotoAndPlay = function (frameNumber, framesToPlay) {
        this._currentTime = frameNumber;

        this._texture = this._textures[this.currentFrame];
        this._textureID = -1;
        
        if (framesToPlay !== undefined) {
            if (framesToPlay > this._textures.length - frameNumber - 1) {
                framesToPlay = this._textures.length - frameNumber - 1;
            }
            
            this._framesToPlay = framesToPlay;
            this._startFrame = frameNumber;
        }
        
        if (this.onFrameChange) {
            this.onFrameChange(this.currentFrame);
        }
        
        this.play();
    };
    
    /*
     * Updates the object transform for rendering
     * @private
     */
    Animation.prototype.update = function (deltaTime) {
        var elapsed = this.animationSpeed * deltaTime;
        
        var _curFrame = this.currentFrame;
        
        if (this._durations !== null) {
            var lag = this._currentTime % 1 * this._durations[this.currentFrame];
            
            lag += elapsed / 60 * 1000;
            
            while (lag < 0) {
                this._currentTime--;
                lag += this._durations[this.currentFrame];
            }
            
            var sign = Math.sign(this.animationSpeed * deltaTime);
            this._currentTime = Math.floor(this._currentTime);
            
            while (lag >= this._durations[this.currentFrame]) {
                lag -= this._durations[this.currentFrame] * sign;
                this._currentTime += sign;
            }
            
            this._currentTime += lag / this._durations[this.currentFrame];
        }
        else {
            this._currentTime += elapsed;
        }
        
        if (this._currentTime < 0 && !this.loop) {
            this.gotoAndStop(0);
            
            if (this.onComplete) {
                this.onComplete();
            }
        }
        else if (this._currentTime >= this._textures.length && !this.loop) {
            this.gotoAndStop(this._textures.length - 1);
            
            if (this.onComplete) {
                this.onComplete();
            }
        }
        else {
            if (!this.loop && this._framesToPlay !== undefined && this._framesToPlay !== null && this._framesToPlay > 0) {
                if (this.currentFrame >= this._startFrame + this._framesToPlay) {
                    this.gotoAndStop(this._startFrame + this._framesToPlay);
                    
                    if (this.onComplete) {
                        this.onComplete();
                    }
                }
            }
            
            this._texture = this._textures[this.currentFrame];
            this._textureID = -1;
            
            if (_curFrame !== this.currentFrame && this.onFrameChange) {
                this.onFrameChange(this.currentFrame);
            }
        }
    };
    
    /*
     * Stops the Animation and destroys it
     *
     */
    Animation.prototype.destroy = function() {
        this.stop();
        PIXI.Sprite.prototype.destroy.call(this);
    };
    
    /**
     * A short hand way of creating a movieclip from an array of frame ids
     *
     * @static
     * @param frames {string[]} the array of frames ids the movieclip will use as its texture frames
     */
    Animation.fromFrames = function(frames) {
        var textures = [];
        
        for (var i = 0; i < frames.length; ++i) {
            textures.push(PIXI.Texture.fromFrame(frames[i]));
        }
        
        return new Animation(textures);
    };
    
    /**
     * A short hand way of creating a movieclip from an array of image ids
     *
     * @static
     * @param images {string[]} the array of image urls the movieclip will use as its texture frames
     */
    Animation.fromImages = function(images) {
        var textures = [];
        
        for (var i = 0; i < images.length; ++i) {
            textures.push(PIXI.Texture.fromImage(images[i]));
        }
        
        return new Animation(textures);
    };

    /**
     * A short hand way of creating a movieclip by specifying frame name format and number of frames
     * @static
     * @param {String} nameFormat Frame name string format
     * @param {Number} framesCount Number of frames
     * */
    Animation.byFrameName = function (nameFormat, framesCount) {
        if (typeof nameFormat === 'string' && framesCount !== undefined && typeof framesCount === 'number') {
            var textures = [];
            for (var i = 0; i < framesCount; ++i) {
                textures.push(PIXI.utils.TextureCache[nameFormat.$(i)]);
            }
            return new Animation(textures);
        }
        else if (Object.prototype.toString.call(nameFormat) === '[object Array]'){
            return Animation.fromFrames(nameFormat);
        }
    };
    
    /** Converts C++ notion of animation "delay" to Pixi's "animation speed" to make resulting animation play the same as it would be played with C++::TAnimation.
     * @example
     * var animation = Animation.byFrameName( 'dragon_breath_%03.png', framesCount );
     * animation.animationSpeed = Animation.animationSpeedByFrameDelay( 0.2 ); //5 frames per second will be played
    */
    Animation.animationSpeedByFrameDelay = function( cppDelay ) {
        return ( 1 / cppDelay ) / 60.0;
    };
    
    global.Animation = Animation;
}(typeof module === 'object' && module.exports || this));


//////////////////
// WEBPACK FOOTER
// ./src/library/animation.js
// module id = 7
// module chunks = 0